import React, { useEffect } from "react";
import { XOctagon, CircleAlert } from "lucide-react";
import { VIDEO_CALL_END_REASONS } from "@/constants/video-call";

interface ViolationLimitWarningProps {
  show: boolean;
  violationType?: "tab_change" | "window_blur";
  onWarningComplete?: () => void;
  violationCount?: number;
  roomDisconnected?: boolean;
  callId?: string;
  organizationId?: string;
}

export const ViolationLimitWarning: React.FC<ViolationLimitWarningProps> = ({
  show,
  violationType,
  onWarningComplete,
  violationCount = 3,
  roomDisconnected = false,
  callId,
  organizationId,
}) => {
  // Debug logging
  useEffect(() => {
    if (show) { // Only log when actually shown
      console.log("🔍 [ViolationLimitWarning] COMPONENT PROPS (ACTIVE):");
      console.log("  - show:", show);
      console.log("  - violationType:", violationType);
      console.log("  - violationCount:", violationCount);
      console.log("  - roomDisconnected:", roomDisconnected);
      console.log("  - callId:", callId);
      console.log("  - callId type:", typeof callId);
      console.log("  - callId is null/undefined?", callId === null || callId === undefined);
      console.log("  - organizationId:", organizationId);
      console.log("  - organizationId type:", typeof organizationId);
    }
  }, [show, violationType, violationCount, roomDisconnected, callId, organizationId]);

  // Auto-trigger callback immediately when shown
  useEffect(() => {
    if (show && onWarningComplete) {
      // Call onWarningComplete immediately to end the call
      onWarningComplete();
    }
  }, [show, onWarningComplete]);

  // Auto-navigate to interview complete after 3 seconds - but only after room disconnects
  useEffect(() => {
    if (!show || !roomDisconnected) {
      console.log("🔍 [ViolationLimitWarning] WAITING FOR CONDITIONS:");
      console.log("  - show:", show);
      console.log("  - roomDisconnected:", roomDisconnected);
      console.log("  - waiting for room disconnect before navigation");
      return;
    }

    console.log("🔍 [ViolationLimitWarning] STARTING NAVIGATION TIMER:");
    console.log("  - room disconnected, starting 3s timer");
    const timer = setTimeout(() => {
      console.log("🔍 [ViolationLimitWarning] NAVIGATION TRIGGERED:");
      console.log("  - auto-navigating to interview complete after room disconnect");
      
      // Store violation data in session storage
      const endReason = violationType === "tab_change" ? 
        VIDEO_CALL_END_REASONS.TAB_CHANGE_VIOLATION : 
        violationType === "window_blur" ? 
          VIDEO_CALL_END_REASONS.WINDOW_BLUR_VIOLATION : 
          VIDEO_CALL_END_REASONS.TOTAL_VIOLATION;
      
      const violationData = {
        endReason,
        violationCount,
        violationType: violationType || 'violation_limit',
        timestamp: new Date().toISOString(),
      };
      
      sessionStorage.setItem('interviewViolationData', JSON.stringify(violationData));
      
      // Navigate to interview complete page with fallback call ID
      const effectiveCallId = callId || `fallback-${Date.now()}`;
      
      console.log("🔍 [ViolationLimitWarning] NAVIGATION PARAMETERS:");
      console.log("  - original callId:", callId);
      console.log("  - effectiveCallId:", effectiveCallId);
      console.log("  - is fallback?", !callId);
      console.log("  - organizationId:", organizationId);
      console.log("  - endReason:", endReason);
      console.log("  - violationCount:", violationCount);
      console.log("  - violationType:", violationType);
      
      const params = new URLSearchParams({
        endReason,
        violations: violationCount.toString(),
        violationType: violationType || 'violation_limit',
        callId: effectiveCallId,
        organizationId: organizationId || '',
      });
      
      const finalUrl = `/interview-complete?${params.toString()}`;
      console.log("🔍 [ViolationLimitWarning] FINAL NAVIGATION:");
      console.log("  - URL:", finalUrl);
      console.log("  - params string:", params.toString());
      
      window.location.replace(finalUrl);
    }, 3000); // 3 second delay

    return () => clearTimeout(timer);
  }, [show, roomDisconnected, violationType, violationCount, callId, organizationId]);

  if (!show) return null;

  const getContent = () => {
    if (violationType === "tab_change") {
      return {
        description: "You have exceeded the maximum allowed tab changes (3) during this proctored session.",
      };
    } else if (violationType === "window_blur") {
      return {
        description: "You have exceeded the maximum allowed window focus losses (3) during this proctored session.",
      };
    } else {
      return {
        description: "You have exceeded the maximum allowed violations during this proctored session.",
      };
    }
  };

  const content = getContent();

  return (
    <div className="fixed inset-0 bg-black/90 backdrop-blur-sm z-[10000] flex items-center justify-center p-4">
      <div className="w-[480px] h-[338px] p-10 bg-[#131314] rounded-3xl border border-[#ec3f3f] flex flex-col justify-start items-start gap-2.5 overflow-hidden">
        <div className="self-stretch h-[258px] flex flex-col justify-center items-center gap-10">
          {/* Main content section */}
          <div className="self-stretch h-[180px] flex flex-col justify-start items-center gap-3">
            {/* Icon container with background circle */}
            <div className="w-[60px] h-[60px] relative flex justify-center items-center">
              <div className="w-[60px] h-[60px] absolute bg-[#ec4040]/20 rounded-full" />
              <XOctagon
                className="w-7 h-7 text-[#ec4040] z-10"
                strokeWidth={2}
                aria-label="Violation limit exceeded icon"
              />
            </div>

            {/* Title and subtitle section */}
            <div className="self-stretch h-[52px] flex flex-col justify-start items-center gap-1">
              <h1 className="text-center text-white text-2xl font-bold font-satoshi leading-7">
                Violation Limit Exceeded
              </h1>
              <h2 className="self-stretch text-center text-white text-base font-normal font-satoshi leading-tight">
                Too many window focus violations
              </h2>
            </div>

            {/* Description text */}
            <p className="self-stretch text-center text-white/70 text-base font-normal font-satoshi leading-normal">
              {content.description}
            </p>
          </div>

          {/* Alert */}
          <div className="px-4 py-2 bg-[#ec4040]/20 rounded flex justify-start items-center gap-2">
            <CircleAlert
              className="w-5 h-5 text-[#ec4040]"
              strokeWidth={2}
              aria-hidden="true"
            />
            <span className="text-center text-[#ec4040] text-base font-normal font-satoshi">
              Interview Ended
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};